import React, {useContext, useMemo} from "react";
import {Navigate, Outlet, useLocation, useNavigate} from "react-router-dom";
import {Context} from "../../store/Store";
import TabBar from "../TabBar/TabBar";
import {sectionPath, tabsFor, tabSlugFromPath} from "./sectionPaths";

/**
 * A section divided into tabs, where the tab is part of the URL.
 *
 * Bugs, Support and Project Settings each had their own copy of this - a
 * `useState` index, a TabBar wired to it, and a chain of comparisons picking
 * what to render. Same shape three times, and each copy had drifted its own
 * way: Bugs inferred its tab from whether a bug id was in the URL, and Settings
 * stored a bare index which it then clamped against a permission-filtered list,
 * so the same stored number meant a different tab depending on who was looking.
 *
 * Now the tab is a path segment (see sectionPaths.js's SECTION_TABS) and the
 * router renders it through the <Outlet/> below, so none of that state exists
 * to go wrong. `emptyLabel` is what to say when the viewer holds none of the
 * permissions the tabs require.
 */
export default props => {
    const {slug, emptyLabel, theme = "dark"} = props;
    const [state] = useContext(Context);
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const projectId = state.project_id;

    const config = tabsFor(slug);

    // Empty permissions means "not loaded yet" rather than "none" - same
    // convention as Sidebar.js's canView, so the tab bar doesn't flash empty
    // on every page load.
    const tabs = useMemo(() => {
        const permissions = state.permissions;
        if (!permissions || permissions.length === 0) return config.tabs;
        return config.tabs.filter((tab) => !tab.permission || permissions.includes(tab.permission));
    }, [state.permissions, config.tabs]);

    const current = tabSlugFromPath(pathname);

    if (tabs.length === 0) {
        return (
            <div className={config.containerClass}>
                <div className={"tabs-container"}>
                    <div className={config.contentClass}>{emptyLabel}</div>
                </div>
            </div>
        );
    }

    // A URL naming no tab, or one this viewer can't see, resolves to the first
    // tab they can. `replace`, so Back doesn't bounce off the redirect.
    if (!current || !tabs.some((tab) => tab.slug === current)) {
        const fallback = tabs.some((tab) => tab.slug === config.defaultTab) ? config.defaultTab : tabs[0].slug;
        return <Navigate to={sectionPath(projectId, slug, {tab: fallback})} replace/>;
    }

    return (
        // Two wrappers, both load-bearing: each section's stylesheet sizes
        // itself off them (see bugs.css, support.css, projectSettings.css).
        <div className={config.containerClass}>
        <div className={"tabs-container"}>
            <TabBar
                tabs={tabs.map((tab) => ({key: tab.slug, label: tab.label}))}
                selected={current}
                // Switching tab is a navigation now, so it goes in the
                // history: Back returns to the tab you came from.
                onSelect={(tabSlug) => navigate(sectionPath(projectId, slug, {tab: tabSlug}))}
                theme={theme}
            />
            <div className={config.contentClass}>
                <Outlet/>
            </div>
        </div>
        </div>
    )
}
